import { MAX_CHAT_DAY_GUESTS } from '@chat-emp/shared';

import { sendMessageTo } from '../data/source.js';
import type { ChannelId, CtaPayload } from '../fixtures/domain.js';
import { runBulk, type BulkResult } from '../inbox/bulkSend.js';
import { formatEventDate, formatTimeRange } from '../util/format.js';
import { readVenue } from './location.js';

/**
 * Telling the guests. A booking that exists only on the employer's calendar is
 * not an invitation.
 *
 * Sent from the client rather than the BFF for one reason: the message has to
 * come *from the employer*, and the employer's own Stream connection is already
 * here. It goes out through `sendMessageTo` and not the live send path — that
 * one fires the first-reply reward, the host `message-sent` emit and per-message
 * analytics, all wrong to replay twenty-four times.
 */

/** One guest, and the chat their invite goes into. */
export interface ChatDayInvite {
  channelId: ChannelId;
  /**
   * calendar-service's invitee row id — what their answer is recorded against.
   *
   * Optional for the same reason the DTO's is: a pre-invitee appointment has no
   * row. A guest without one gets the invite with no buttons rather than buttons
   * that would answer on nobody's behalf.
   */
  inviteeId?: number;
  name: string;
}

export interface ChatDayInviteInput {
  appointmentId: number;
  title: string;
  startsAt: string;
  endsAt: string;
  /**
   * The venue, exactly as `CalendarEventDto.location` holds it — which for a
   * Chat Day is empty, because a Chat Day happens in the chats. Passed in rather
   * than read off the event so the caller can name a venue the record does not
   * carry; `readVenue` turns empty into "In this chat" either way.
   */
  location: string;
  /** What to expect on the day. Rides the invite, under the when and where. */
  description?: string;
  /** The employer's covering note. Its own message — see `sendChatDayInvites`. */
  message?: string;
  invitees: ChatDayInvite[];
  /**
   * Where Accept / Decline post. Injected, never decided here: the handler is
   * chat-js's, this app must not hold its hostname, and an empty string is the
   * honest answer while it does not exist yet — in which case the invite goes
   * out with no buttons.
   */
  responderBaseUrl: string;
}

/** The glyph that opens the venue line, by what kind of place it is. */
const VENUE_MARK = { chat: '💬', link: '🔗', place: '📍' } as const;

/**
 * The invite, as the words a message carries.
 *
 * Markdown, because that is all a message in this app is — `RichText` allows
 * bold, italics, links, lists and line breaks and nothing else, so a card that
 * rendered anything richer would be a design the wire cannot send.
 *
 * Four things in the order they get asked: what it is, when, where, and what to
 * expect. The description is the whole reason that field exists on the form, so
 * it is the body rather than a line — separated by a blank line so a long one
 * reads as prose rather than as a fourth label.
 */
export function inviteText(input: {
  title: string;
  startsAt: string;
  endsAt: string;
  location: string;
  description?: string;
}): string {
  const venue = readVenue(input.location);
  // Blank lines, not single newlines. This card is written for the *candidate*,
  // and their side renders through the SDK's default `renderText`, which follows
  // CommonMark and collapses a lone newline to a space — the asymmetry
  // `docs/TODO.md` records under "stream-chat parity". Joined with `\n` the three
  // head lines arrive as one run-on line: "**Barista Tuesday** Tue, 25 Aug 2026 ·
  // 14:00 – 16:00 💬 In this chat". A blank line is a paragraph break in both
  // renderers, so the layout survives whichever app the guest is reading in.
  const head = [
    `**${input.title}**`,
    `${formatEventDate(input.startsAt)} · ${formatTimeRange(input.startsAt, input.endsAt)}`,
    `${VENUE_MARK[venue.kind]} ${venue.label}`,
  ].join('\n\n');

  const body = input.description?.trim();
  return body ? `${head}\n\n${body}` : head;
}

/**
 * The answerable row under the invite, or null.
 *
 * Null on three counts, and they all mean the same thing: there is nowhere for
 * an answer to go. No responder configured (chat-js has not shipped its
 * handler), no invitee row to record against, or no usable appointment id — the
 * last because the id crosses from a string on the DTO to the number the
 * upstream keys on, and a payload reading `{"appointmentId": null}` is a button
 * that answers about nothing. A row that resolves to nothing is worse than no
 * buttons: it invites a press and then fails it.
 *
 * No `excludeUserIds`. The sender is barred by `canClickCta` already, which is
 * the right rule and one nobody has to remember: an employer who could press
 * Accept on their own invitation could fill their calendar with acceptances
 * nobody made.
 *
 * "Decline", not "Reject". ajt2.5's live interview invite says decline and
 * treats reject as legacy; one vocabulary across both invite kinds.
 */
export function inviteCta(
  responderBaseUrl: string,
  appointmentId: number,
  inviteeId: number | undefined,
): CtaPayload | null {
  if (!responderBaseUrl || inviteeId === undefined) return null;
  if (!Number.isInteger(appointmentId) || appointmentId <= 0) return null;

  const base = responderBaseUrl.replace(/\/+$/, '');
  const payload = { appointmentId, inviteeId };
  return {
    completed: false,
    buttons: [
      {
        id: 'accept',
        label: 'Accept',
        style: 'primary',
        mode: 'url',
        method: 'POST',
        url: `${base}/accept`,
        payload,
      },
      {
        id: 'decline',
        label: 'Decline',
        style: 'secondary',
        mode: 'url',
        method: 'POST',
        url: `${base}/decline`,
        payload,
      },
    ],
  };
}

/**
 * Put the invite — and the covering note, where one was written — into every
 * guest's own chat.
 *
 * **Two messages, not one.** The invite is a record that will gain a state: it
 * gets answered, and its button row greys when it does. The note is a person
 * talking, and nothing should ever grey it out or rewrite it. Appending it to
 * the invite's text would tie the two together forever, so it is its own bubble,
 * sent after — the record first, then the words about it.
 *
 * **One chat each, never a group room.** Every jobseeker keeps their private
 * thread; a Chat Day is a batch of invitations rather than a broadcast, and no
 * guest learns who else was asked.
 *
 * **A guest is done only when everything for them landed.** If the invite
 * arrives and the note does not, that guest is reported as failed. Truthful and
 * slightly pessimistic beats the alternative: an employer told all twenty-four
 * were notified, when three of them never saw the sentence explaining what the
 * day is for.
 *
 * Failures are collected rather than thrown — the Chat Day is already booked and
 * there is no unbooking, so one dead channel must not cost the other twenty
 * three their invite.
 */
export async function sendChatDayInvites(
  input: ChatDayInviteInput,
): Promise<BulkResult<ChatDayInvite>> {
  const text = inviteText(input);
  const note = input.message?.trim();

  return runBulk(
    input.invitees,
    async (guest) => {
      const cta = inviteCta(input.responderBaseUrl, input.appointmentId, guest.inviteeId);
      await sendMessageTo(guest.channelId, text, cta ?? undefined);
      if (note) await sendMessageTo(guest.channelId, note);
    },
    undefined,
    // Explicit, because a Chat Day's guest list is its own limit and has nothing
    // to do with the inbox dialog's. Left implicit, a booking that grew past
    // runBulk's ceiling would silently drop the guests it names.
    { cap: MAX_CHAT_DAY_GUESTS },
  );
}
